<template>
  <div class="tw:w-full tw:min-h-screen">
    <HotelOffersBanner v-if="!hasAnyOffers" @openCreateOfferOpen="openCreateOfferOpen" />
    <ContainerLayout class="tw:flex-col">
      <ActiveOfferCard
        v-if="hasActiveOffer"
        :activeOffer="activeOffer"
        @openRemoveModal="openRemoveOfferModal"
        @openEditOffer="openEditOffer"
      />
      <HotelOffersGrid
        :type="getOfferGridType"
        :filteredTemplates="filteredTemplates"
        :filteringCategories="filteringCategories"
        :selectedFilter="selectedFilter"
        :icons="icons"
        @selectFilter="selectFilter"
        @openSuggestionsModal="openSuggestionsModal"
        @openCreateOfferOpen="openCreateOfferOpen"
      />
      <PastOffers
        v-if="!isLoading && pastOffers.length > 0"
        :pastOffers="pastOffers"
        :hasActiveOffer="hasActiveOffer"
      />
      <RemoveOffer :isOpen="isRemoveOfferOpen" @closeModal="closeRemoveOfferModal" />
      <HotelOffersSuggestions
        :isOpen="isSuggestionsOpen"
        :source="suggestionsModalSource"
        @closeModal="closeSuggestionsModal"
        @openCreateOfferOpen="openCreateOfferOpen"
        :offer="selectedOffer"
      />
      <CreateNewOffer
        :activeOffer="activeOffer"
        :hasCancellationDate="hasCancellationDate"
        :type="createOfferMode"
        :hasActiveOffer="hasActiveOffer"
        :isOpen="isCreateOfferOpen"
        :offer="selectedOffer"
        :icons="icons"
        :filteringCategories="filteringCategories"
        :templates="templates"
        :source="newOfferModalSource"
        @selectOffer="onSelectOffer"
        @closeModal="closeCreateOfferOpen"
        @openSuggestionsModal="openSuggestionsModal"
      />
    </ContainerLayout>
  </div>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import HotelOffersBanner from '@/components/hotelOffers/HotelOffersBanner.vue'
import HotelOffersGrid from '@/components/hotelOffers/HotelOffersGrid.vue'
import CreateNewOffer from '@/components/hotelOffers/createOffer/CreateNewOffer.vue'
import HotelOffersSuggestions from '@/components/hotelOffers/HotelOffersSuggestions.vue'
import ActiveOfferCard from '@/components/hotelOffers/ActiveOfferCard.vue'
import RemoveOffer from '@/components/hotelOffers/RemoveOffer.vue'
import ContainerLayout from '@/layouts/ContainerLayout.vue'
import PastOffers from '@/components/hotelOffers/PastOffers.vue'
import icons from '@/constants/hotelOffersIcons.ts'

import { selectedAccommodationId } from '@/layouts/queries'
import { useOffersTemplates, useActiveOffer, usePastOffers } from '@/components/hotelOffers/queries'
import { useStudioPlusCampaign } from '@/components/payment/queries'
import { getDatesDifference } from '@/utils/hotelOffersUtils.ts'
import { useHotelOffers } from '@/components/hotelOffers/useHotelOffers'

import appAnalytics from '@/utils/tracking'

const route = useRoute()
const { templates, categories } = useOffersTemplates()
const { hasActiveOffer, activeOffer, hasAnyOffers } = useActiveOffer(selectedAccommodationId)
const { studioPlusCampaign } = useStudioPlusCampaign(selectedAccommodationId)
const { isLoading, pastOffers } = usePastOffers()

const {
  openRemoveOfferModal,
  closeRemoveOfferModal,
  closeCreateOfferOpen,
  selectOffer,
  closeSuggestionsModal,
  isRemoveOfferOpen,
  categoryNames,
  selectedOffer,
  isCreateOfferOpen,
  createOfferMode,
  newOfferModalSource,
  suggestionsModalSource,
  isSuggestionsOpen,
} = useHotelOffers()

const openEditOffer = activeOffer => {
  createOfferMode.value = 'edit'
  if (activeOffer.template !== null) {
    selectOffer(activeOffer.template, templates)
  }
  isCreateOfferOpen.value = true
}
const openCreateOfferOpen = ({ offerName, source }) => {
  if (activeOffer.value && activeOffer.value?.template !== offerName && offerName !== null) {
    createOfferMode.value = 'replace'
  } else if (activeOffer.value && activeOffer.value?.template === offerName) {
    createOfferMode.value = 'edit'
  } else {
    createOfferMode.value = 'create'
  }
  if (offerName !== null) {
    selectOffer(offerName, templates)
  }

  newOfferModalSource.value = source
  isCreateOfferOpen.value = true
}

const openSuggestionsModal = source => {
  appAnalytics.track(appAnalytics.events.HO_FEEDBACK_LINK_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    source: source === 'popup' ? 'Offer Setup Step 1' : 'Offers Grid Page',
  })
  suggestionsModalSource.value = source
  isSuggestionsOpen.value = true
}
const onSelectOffer = offerName => {
  selectOffer(offerName, templates)
}

const getOfferGridType = computed(() => {
  if (hasActiveOffer.value) return 'explore'
  if (hasAnyOffers.value) return 'create'
  else return 'firstVisit'
})

const selectedFilter = ref('all')
const filteringCategories = computed(() => {
  let allCategories = [{ value: 'all', label: categoryNames['all'] }]
  let newCategories =
    categories.value?.length > 0
      ? categories.value.map(category => {
          return {
            value: category,
            label: categoryNames[category] ?? category,
          }
        })
      : []

  return [...allCategories, ...newCategories]
})

const selectFilter = selectedValue => {
  appAnalytics.track(appAnalytics.events.HO_CATEGORY_FILTERS_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    selectedFilter: selectedValue,
  })
  selectedFilter.value = selectedValue
}

const filteredTemplates = ref([])

watch(
  [templates, selectedFilter],
  ([newTemplates, filter]) => {
    if (filter === 'all') {
      filteredTemplates.value = newTemplates
    } else {
      filteredTemplates.value = newTemplates.filter(item => item.category === filter)
    }
  },
  { immediate: true }
)

const hasCancellationDate = computed(() => {
  if (studioPlusCampaign.value?.endDate) {
    const studioCancellationDate = new Date(studioPlusCampaign.value.endDate.replace('@', 'T'))
    const activeOfferCancellationDate = new Date(activeOffer.value?.expirationDate?.replace('@', 'T'))
    const datesDifference = getDatesDifference(studioCancellationDate, activeOfferCancellationDate)
    if (datesDifference === 0 || datesDifference === 1) return false
    else return true
  } else {
    return activeOffer.value?.expirationDate === null ? false : true
  }
})

onMounted(() => {
  if (route.query.create_offer) {
    openCreateOfferOpen({ offerName: null, source: 'plus-insights' })
  }
})

watch(
  route,
  newRoute => {
    if (newRoute.query.create_offer) {
      openCreateOfferOpen({ offerName: null, source: 'plus-insights' })
    }
  },
  { deep: true }
)
</script>
